import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { Newsletter } from '../../domain/models/newsletter.model';
import { NewsletterInput } from '../../domain/models/newsletter-input.model';
import { INewslettersRepository } from '../../domain/repositories/newsletters.repository';
import { NewslettersRemoteDatasource } from '../datasources/remote/newsletters.remote-datasource';
import { NewsletterMapper } from '../mappers/newsletter.mapper';

@Injectable()
export class NewslettersRepositoryImpl implements INewslettersRepository {
  private readonly datasource = inject(NewslettersRemoteDatasource);
  private readonly mapper = inject(NewsletterMapper);

  public list(organizationId: number): Observable<readonly Newsletter[]> {
    return this.datasource
      .list(organizationId)
      .pipe(map((dtos) => dtos.map((dto) => this.mapper.toNewsletter(dto))));
  }

  public getById(organizationId: number, newsletterId: string): Observable<Newsletter> {
    return this.datasource
      .getById(organizationId, newsletterId)
      .pipe(map((dto) => this.mapper.toNewsletter(dto)));
  }

  public create(organizationId: number, input: NewsletterInput): Observable<Newsletter> {
    return this.datasource
      .create(organizationId, this.mapper.toCreateRequest(input))
      .pipe(map((dto) => this.mapper.toNewsletter(dto)));
  }

  public update(
    organizationId: number,
    newsletterId: string,
    input: NewsletterInput,
  ): Observable<Newsletter> {
    return this.datasource
      .update(organizationId, newsletterId, this.mapper.toCreateRequest(input))
      .pipe(map((dto) => this.mapper.toNewsletter(dto)));
  }

  public delete(organizationId: number, newsletterId: string): Observable<void> {
    return this.datasource.delete(organizationId, newsletterId);
  }

  public publish(
    organizationId: number,
    newsletterId: string,
    notifyByPush: boolean,
  ): Observable<Newsletter> {
    return this.datasource
      .publish(organizationId, newsletterId, { NotifyByPush: notifyByPush })
      .pipe(map((dto) => this.mapper.toNewsletter(dto)));
  }

  public archive(organizationId: number, newsletterId: string): Observable<Newsletter> {
    return this.datasource
      .archive(organizationId, newsletterId)
      .pipe(map((dto) => this.mapper.toNewsletter(dto)));
  }
}
